import {
    CompositionLayoutType,
    TCompositionContentTypesWithoutCompanyInfo,
    TCompositionLayoutType
} from '~/types/builder.types';
import { ICompositionLayoutTypeConfig } from '~/components/Builder/BuilderSidebar/ContainerSidebar/composition-layout.types';
import {
    COMPOSITION_LAYOUTS,
    DEFAULT_EXCLUDED_LAYOUT_TYPES
} from '~/components/Builder/BuilderSidebar/ContainerSidebar/composition-layouts.data';
import {
    CONTENT_TYPES_LAYOUTS_ORDER,
    DEFAULT_LAYOUT_PER_CONTENT_TYPE
} from '~/components/Builder/BuilderSidebar/ContainerSidebar/composition-layouts-sidebar-data';
import { Flow } from '~/models/flow.model';
import {
    changeBlockLayoutLimitations,
    checkLimitations
} from '~/components/Builder/Common/CreationLimitations/creationLimitations';
import { BlockLike } from '~/components/Builder/Common/CreationLimitations/creationLimitations.types';

const _getProcessedCompositionLayoutsByContentType = (
    contentType: TCompositionContentTypesWithoutCompanyInfo
) => {
    const layouts: Partial<typeof COMPOSITION_LAYOUTS> = Object.values(
        COMPOSITION_LAYOUTS
    ).reduce((acc, currLayout) => {
        return CONTENT_TYPES_LAYOUTS_ORDER[contentType][currLayout.type]
            ? {
                  ...acc,
                  [currLayout.type]: {
                      ...currLayout,
                      order:
                          CONTENT_TYPES_LAYOUTS_ORDER[contentType][
                              currLayout.type
                          ] ?? 100
                  }
              }
            : acc;
    }, {});

    return layouts;
};

export function getCompositionLayouts({
    compositionContentType,
    compositionLayoutType,
    forceGetLayoutTypes = [],
    getAllLayoutTypes = false,
    selectedFlow
}: {
    compositionContentType?: TCompositionContentTypesWithoutCompanyInfo;
    compositionLayoutType?: TCompositionLayoutType;
    forceGetLayoutTypes?: TCompositionLayoutType[];
    getAllLayoutTypes?: boolean;
    selectedFlow?: Flow;
} = {}): {
    defaultLayout: ICompositionLayoutTypeConfig;
    layouts:
        | Record<CompositionLayoutType, ICompositionLayoutTypeConfig>
        | Partial<Record<CompositionLayoutType, ICompositionLayoutTypeConfig>>;
} {
    const layouts = compositionContentType
        ? _getProcessedCompositionLayoutsByContentType(compositionContentType)
        : COMPOSITION_LAYOUTS;

    const excludedLayoutTypes = getAllLayoutTypes
        ? []
        : DEFAULT_EXCLUDED_LAYOUT_TYPES.filter(
              layoutType => !forceGetLayoutTypes.includes(layoutType)
          );

    const layoutsAfterLimitations = applyLimitations(layouts, selectedFlow);

    const filteredLayouts = Object.fromEntries(
        Object.entries(layoutsAfterLimitations).filter(
            ([, layout]) => !excludedLayoutTypes.includes(layout.type)
        )
    );

    const defaultLayoutKey =
        compositionLayoutType ||
        (compositionContentType
            ? DEFAULT_LAYOUT_PER_CONTENT_TYPE[compositionContentType]
            : CompositionLayoutType.IconAndText);

    const defaultLayout = filteredLayouts[defaultLayoutKey];

    return {
        defaultLayout,
        layouts: filteredLayouts
    };
}

function applyLimitations(
    layouts: Partial<
        Record<CompositionLayoutType, ICompositionLayoutTypeConfig>
    >,
    selectedFlow?: Flow
) {
    return Object.fromEntries(
        Object.entries(layouts).map(([layoutType, layout]) => {
            const limitation = checkLimitations(changeBlockLayoutLimitations, {
                sourceBlock: transformLayoutStructToContainerBlockLike(
                    layout.layoutStruct
                ),
                targetFlow: selectedFlow
            });
            return [
                layoutType,
                {
                    ...layout,
                    disabled: limitation.status === 'disabled',
                    disabledTooltipText:
                        limitation.status === 'disabled'
                            ? limitation.reason
                            : ''
                }
            ];
        })
    );
}

function transformLayoutStructToContainerBlockLike(
    layoutStruct: ICompositionLayoutTypeConfig['layoutStruct']
) {
    const { children, type, question } = layoutStruct;
    return {
        type: type,
        question: {
            question_type: question?.question_type
        },
        isQuestionBlock: () => type === 'question_block',
        ...(children && {
            childBlocks: children.map(transformLayoutStructToContainerBlockLike)
        })
    } as BlockLike;
}
